import type { OrderServicerInfo } from '@/types/order';

export const DURATION_ALLOCATION_TOLERANCE = 0.01;

export interface DurationAllocationRow extends OrderServicerInfo {
  duration: number;
  /** 工时 × 时薪 */
  amount: number;
}

function readHourlyWage(servicer: OrderServicerInfo): number {
  const wage = servicer.hourlyWage;
  return typeof wage === 'number' && Number.isFinite(wage) && wage > 0 ? wage : 0;
}

export function hasSettleHourlyWage(servicer: Pick<OrderServicerInfo, 'hourlyWage'>): boolean {
  return readHourlyWage(servicer as OrderServicerInfo) > 0;
}

export interface DurationAllocationPreview {
  totalHours: number;
  allocatedHours: number;
  /** 订单初始金额（不可变更） */
  orderInitialAmount: number;
  /** 员工劳务费合计（时薪 × 分配工时） */
  totalLaborCost: number;
  laborExceedsBudget: boolean;
  rows: DurationAllocationRow[];
  isValid: boolean;
}

export interface DurationAllocationPayload {
  operator_id: number | string;
  duration: number;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function normalizeSettleOperatorId(id: number | string): string {
  return String(id).trim();
}

/** 单人劳务费 = 时薪 × 分配工时 */
export function calcServicerLaborCost(
  duration: number,
  hourlyWage: number | undefined,
): number {
  const hours = round2(duration);
  const wage = readHourlyWage({ hourlyWage } as OrderServicerInfo);
  if (hours <= 0 || wage <= 0) return 0;
  return round2(hours * wage);
}

/** 订单总额 = 所有工作人员劳务费之和 */
export function calcSettleOrderTotal(
  rows: Array<Pick<DurationAllocationRow, 'amount'>>,
): number {
  return round2(rows.reduce((sum, row) => sum + row.amount, 0));
}

function floorStep(value: number, step: number): number {
  if (step <= 0) return round2(value);
  return Math.floor(value / step) * step;
}

/** 在预算与总工时约束下，计算某员工可分配的最大工时 */
export function getMaxOperatorDuration(
  operatorId: string,
  totalHours: number,
  orderInitialAmount: number,
  servicers: OrderServicerInfo[],
  durationByOperator: Record<string, number>,
  durationStep = 0.5,
): number {
  const servicer = servicers.find((item) => item.operatorId === operatorId);
  if (!servicer || totalHours <= 0) return 0;

  const otherHours = round2(
    servicers.reduce(
      (sum, item) =>
        item.operatorId === operatorId
          ? sum
          : sum + (durationByOperator[item.operatorId] ?? 0),
      0,
    ),
  );
  const maxByHours = round2(totalHours - otherHours);
  if (maxByHours <= 0) return 0;

  const wage = readHourlyWage(servicer);
  if (wage <= 0 || orderInitialAmount <= 0) return maxByHours;

  const otherLabor = round2(
    servicers.reduce((sum, item) => {
      if (item.operatorId === operatorId) return sum;
      return sum + calcServicerLaborCost(
        durationByOperator[item.operatorId] ?? 0,
        item.hourlyWage,
      );
    }, 0),
  );
  const budgetLeft = round2(orderInitialAmount - otherLabor);
  if (budgetLeft <= 0) return 0;

  const maxByBudget = floorStep(budgetLeft / wage, durationStep);
  return Math.max(0, Math.min(maxByHours, maxByBudget));
}

function roundStep(value: number, step: number): number {
  return Math.round(value / step) * step;
}

/** 限制单人分配工时，使员工劳务费总和不超过订单初始金额 */
export function clampOperatorDuration(
  operatorId: string,
  nextDuration: number,
  totalHours: number,
  orderInitialAmount: number,
  servicers: OrderServicerInfo[],
  durationByOperator: Record<string, number>,
  durationStep = 0.5,
): number {
  const stepped = roundStep(Math.max(0, nextDuration), durationStep);
  const maxAllowed = getMaxOperatorDuration(
    operatorId,
    totalHours,
    orderInitialAmount,
    servicers,
    durationByOperator,
    durationStep,
  );
  return Math.min(stepped, maxAllowed);
}

/** 批量限制各员工工时，使劳务费总和不超过订单初始金额 */
export function clampDurationAllocationsToBudget(
  allocation: Record<string, number>,
  hours: number,
  orderInitialAmount: number,
  servicers: OrderServicerInfo[],
  durationStep = 0.5,
): Record<string, number> {
  if (orderInitialAmount <= 0 || servicers.length === 0 || hours <= 0) return allocation;

  const next: Record<string, number> = { ...allocation };
  for (let pass = 0; pass < servicers.length * 2; pass += 1) {
    let changed = false;
    for (const servicer of servicers) {
      const current = next[servicer.operatorId] ?? 0;
      const clamped = clampOperatorDuration(
        servicer.operatorId,
        current,
        hours,
        orderInitialAmount,
        servicers,
        next,
        durationStep,
      );
      if (clamped !== current) {
        next[servicer.operatorId] = clamped;
        changed = true;
      }
    }
    if (!changed) break;
  }
  return next;
}

export function formatMoney(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export function formatDurationHours(hours: number): string {
  return Number.isInteger(hours) ? `${hours} h` : `${hours.toFixed(1)} h`;
}

/** 订单上的服务人员。优先用 servicers，否则退回 assignees。 */
export function listOrderAssignedStaff(
  order: { servicers?: OrderServicerInfo[]; assignees: Array<number | string> },
): OrderServicerInfo[] {
  if (order.servicers?.length) {
    return order.servicers.map((servicer) => ({
      ...servicer,
      operatorId: normalizeSettleOperatorId(servicer.operatorId),
    }));
  }
  return order.assignees.map((id) => ({
    operatorId: normalizeSettleOperatorId(id),
    name: `Staff #${id}`,
  }));
}

/** 默认将总工时平均分配给各服务人员 */
export function computeEqualDurationAllocations(
  totalHours: number,
  servicers: OrderServicerInfo[],
): Record<string, number> {
  const count = servicers.length;
  if (count === 0 || totalHours <= 0) return {};

  const base = Math.floor((totalHours / count) * 100) / 100;
  const map: Record<string, number> = {};
  servicers.forEach((servicer, index) => {
    map[servicer.operatorId] =
      index === count - 1
        ? round2(totalHours - base * (count - 1))
        : base;
  });
  return map;
}

export function buildDurationAllocationPreview(
  totalHours: number,
  orderInitialAmount: number,
  servicers: OrderServicerInfo[],
  durationByOperator: Record<string, number>,
): DurationAllocationPreview {
  const allocatedHours = round2(
    servicers.reduce((sum, s) => sum + (durationByOperator[s.operatorId] ?? 0), 0),
  );

  const rows: DurationAllocationRow[] = servicers.map((servicer) => {
    const duration = round2(durationByOperator[servicer.operatorId] ?? 0);
    const amount = calcServicerLaborCost(duration, servicer.hourlyWage);
    return { ...servicer, duration, amount };
  });

  const totalLaborCost = calcSettleOrderTotal(rows);
  const laborExceedsBudget =
    orderInitialAmount > 0
    && totalLaborCost - orderInitialAmount > DURATION_ALLOCATION_TOLERANCE;
  const allAllocatedHaveWage = rows.every(
    (row) => row.duration <= 0 || hasSettleHourlyWage(row),
  );
  const isValid =
    servicers.length > 0
    && totalHours > 0
    && orderInitialAmount > 0
    && Math.abs(allocatedHours - totalHours) <= DURATION_ALLOCATION_TOLERANCE
    && allAllocatedHaveWage
    && totalLaborCost > 0
    && !laborExceedsBudget;

  return {
    totalHours,
    allocatedHours,
    orderInitialAmount,
    totalLaborCost,
    laborExceedsBudget,
    rows,
    isValid,
  };
}

export function buildDurationAllocationsPayload(
  rows: DurationAllocationRow[],
): DurationAllocationPayload[] {
  return rows
    .filter((row) => row.duration > 0)
    .map((row) => ({
      operator_id: row.operatorId,
      duration: row.duration,
    }));
}

/** 调整总工时时，按原比例缩放各人员已分配时长 */
export function scaleDurationAllocations(
  durationByOperator: Record<string, number>,
  oldTotal: number,
  newTotal: number,
): Record<string, number> {
  const entries = Object.entries(durationByOperator);
  if (newTotal <= 0 || oldTotal <= 0 || entries.length === 0) return {};

  const ratio = newTotal / oldTotal;
  const scaled: Record<string, number> = {};
  let accumulated = 0;

  entries.forEach(([operatorId, duration], index) => {
    if (index === entries.length - 1) {
      scaled[operatorId] = round2(newTotal - accumulated);
    } else {
      const next = round2(duration * ratio);
      scaled[operatorId] = next;
      accumulated += next;
    }
  });

  return scaled;
}
